import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import type { ReceivedItemDto } from '@tiffinify/contracts';
import { addMonths, formatMonth, formatShort } from '@tiffinify/core';
import { Avatar, Badge, Card, Chip, Chips, Empty, EmptyFilterState, Icon, PageLoader, SearchField, Section, LoadError } from '../components/ui';
import { Amt, Av, Back, Btn2, Card as PCard, Empty as PEmpty, Hero, Ic, Kick, Note, Row, Screen, Sp, Title, Top } from '../components/p';
import { formatInr } from '../lib/format';
import { useMe, useRevenue } from '../lib/queries';

/** Rows shown at first, and added by each "Show more". */
const STEP = 10;

type Kind = 'upi' | 'cash' | 'refund';
const kindOf = (x: ReceivedItemDto): Kind => (x.kind === 'refund' ? 'refund' : x.method);
const KIND_LABEL: Record<Kind, string> = { upi: 'UPI', cash: 'Cash', refund: 'Refund' };

/** Every payment and refund of the month, newest first: search, filter by kind, and more on demand. */
function PaymentsHistory({ items }: { items: ReceivedItemDto[] }) {
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<Kind | 'all'>('all');
  const [shown, setShown] = useState(STEP);

  const counts: Record<Kind, number> = { upi: 0, cash: 0, refund: 0 };
  for (const x of items) counts[kindOf(x)]++;
  const q = search.trim().toLowerCase();
  const filtered = items.filter((x) => (kind === 'all' || kindOf(x) === kind) && (!q || x.name.toLowerCase().includes(q) || String(x.amount).includes(q)));
  const reset = (k: Kind | 'all', s = search) => { setKind(k); setSearch(s); setShown(STEP); };

  return (
    <Section title="Payments and refunds" meta={items.length > 0 ? <Badge>{items.length}</Badge> : undefined}>
      {items.length === 0 ? (
        <Card><Empty title="No payments this month">Payments you record show up here.</Empty></Card>
      ) : (
        <Card>
          <div className="pay-history-toolbar">
            {items.length > STEP && <SearchField value={search} onChange={(v) => reset(kind, v)} label="Search payments" placeholder="Name or amount" />}
            <Chips sm scroll label="Show">
              <Chip on={kind === 'all'} count={items.length} onClick={() => reset('all')}>All</Chip>
              {(['upi', 'cash', 'refund'] as const).filter((k) => counts[k] > 0).map((k) => (
                <Chip key={k} on={kind === k} count={counts[k]} onClick={() => reset(k)}>{k === 'refund' ? 'Refunds' : KIND_LABEL[k]}</Chip>
              ))}
            </Chips>
          </div>

          {filtered.length === 0 ? (
            <EmptyFilterState title="No payments match" desc={q ? `Nothing for “${search.trim()}”.` : 'Nothing of this kind this month.'} onReset={() => reset('all', '')} resetLabel="Show all" />
          ) : (
            <div className="pay-history-list">
              {filtered.slice(0, shown).map((x) => (
                <Link key={x.paymentId} to={`/pay/${x.subscriptionId}`} className="pay-record-link">
                  <Avatar name={x.name} size={40} />
                  <span className="pay-record-mid">
                    <span className="pay-record-name">{x.name}</span>
                    <span className="pay-record-meta">
                      <span className={`pay-method-badge ${kindOf(x)}`}>{KIND_LABEL[kindOf(x)]}</span>
                      {formatShort(x.paidOn)}
                    </span>
                  </span>
                  <span className={`pay-amount${x.kind === 'refund' ? ' refund' : ''}`}>{x.kind === 'refund' ? '−' : ''}{formatInr(x.amount)}</span>
                  <Icon name="right" size={15} />
                </Link>
              ))}
            </div>
          )}

          {filtered.length > shown && (
            <button type="button" className="show-more" onClick={() => setShown((n) => n + STEP)}>
              Show {Math.min(STEP, filtered.length - shown)} more{filtered.length - shown > STEP && <small>· {filtered.length - shown} left</small>}
            </button>
          )}
        </Card>
      )}
    </Section>
  );
}

const MONTH_ONLY = (m: string) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long' });

/** Money (P40): what came in this month from plans, orders and extras, what is still due, and every payment on demand. */
export function Revenue() {
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [history, setHistory] = useState(false);
  const me = useMe();
  const isOwner = me.data?.user.role === 'owner';
  const q = useRevenue(month, isOwner);
  const r = q.data;

  if (me.data && !isOwner) return <Navigate to="/" replace />;

  // The server defaults to this month too, so the header is complete before the numbers arrive.
  const currentMonth = me.data?.today.slice(0, 7) ?? new Date().toISOString().slice(0, 7);
  const m = month ?? r?.month ?? currentMonth;
  const thisMonth = m === currentMonth;
  const total = r ? r.collected + r.orders.collected + r.extras.collected : 0;
  const stillDue = r ? r.pending + r.orders.pending + r.extras.pending : 0;
  const totalText = formatInr(total);
  const part = (n: number) => (total ? `${(n / total) * 100}%` : '0%');

  return (
    <Screen gap={16}>
      <Top>
        <Back to="/more" label="More" />
        <Sp />
        <span className="p-month" role="group" aria-label="Month">
          <button type="button" aria-label="Previous month" onClick={() => setMonth(addMonths(m, -1))}><Icon name="left" size={14} sw={2.4} /></button>
          <b>{MONTH_ONLY(m)}{m.slice(0, 4) !== currentMonth.slice(0, 4) ? ` ${m.slice(0, 4)}` : ''}</b>
          <button type="button" aria-label="Next month" disabled={m >= currentMonth} onClick={() => setMonth(addMonths(m, 1))}><Icon name="right" size={14} sw={2.4} /></button>
        </span>
      </Top>
      <Title h="Money" />
      {q.isError && !r ? (
        <LoadError what="revenue" onRetry={() => void q.refetch()} />
      ) : !r ? (
        <PageLoader label="Adding up payments" />
      ) : (
        <>
          <Hero label={`Money collected in ${formatMonth(r.month)}`}>
            <div className="p-sec"><Kick s>{thisMonth ? 'Collected' : `Collected in ${formatMonth(r.month)}`}</Kick><Kick style={{ color: '#B0A79A' }}>After refunds</Kick></div>
            <span className="p-big" style={{ fontSize: totalText.length > 9 ? 44 : 54 }}>{totalText}</span>
            <div className="p-bar3" aria-hidden="true"><i style={{ width: part(r.collected) }} /><i style={{ width: part(r.orders.collected) }} /><i style={{ width: part(r.extras.collected) }} /></div>
            <div className="p-facts three">
              <div><Kick>Plans</Kick><b>{formatInr(r.collected)}</b></div>
              <div><Kick>Orders</Kick><b>{formatInr(r.orders.collected)}</b></div>
              <div><Kick>Extras</Kick><b>{formatInr(r.extras.collected)}</b></div>
            </div>
          </Hero>

          <div className="p-sec">
            <h2 className="p-h2">Still due · <span style={{ color: stillDue ? 'var(--p-red)' : undefined }}>{formatInr(stillDue)}</span></h2>
            {r.due.length > 0 && <Link to="/remind-all">Remind all {r.due.length}</Link>}
          </div>
          {stillDue === 0 ? (
            <PCard><PEmpty title="Nothing due">Every plan, order and extra is paid up.</PEmpty></PCard>
          ) : (
            <PCard>
              {r.due.map((d) => (
                <Row key={d.subscriptionId} to={`/pay/${d.subscriptionId}`} left={<Av name={d.name} />} t={d.name} label={`Record a payment from ${d.name}`}
                  s={d.paid > 0 ? `Plan · ${formatInr(d.paid)} paid of ${formatInr(d.price)}` : 'Plan · nothing paid yet'} right={<Amt>{formatInr(d.due)}</Amt>} />
              ))}
              {r.orders.pending > 0 && <Row to="/menus" left={<Ic icon="receipt" tone="s" />} t="Orders" s="Unpaid and says-paid orders" right={<Amt>{formatInr(r.orders.pending)}</Amt>} />}
              {r.extras.pending > 0 && <Row left={<Ic icon="plus" tone="s" />} t="Extras" s="Unpaid, on each person’s Money tab" right={<Amt>{formatInr(r.extras.pending)}</Amt>} />}
            </PCard>
          )}
          <Note>{r.activeCount} {r.activeCount === 1 ? 'plan' : 'plans'} running now. Tap someone to record what they paid.</Note>

          {history
            /* Keyed by month: a new month starts at the top, unfiltered. */
            ? <PaymentsHistory key={r.month} items={r.received} />
            : <Btn2 label={`See payments received${r.received.length ? ` · ${r.received.length}` : ''}`} onClick={() => setHistory(true)} />}
        </>
      )}
    </Screen>
  );
}
